import { useRef, useEffect, useCallback } from 'react';
import anamConfig from '../config/anamConfig';

function decodeAudioDataSafe(audioCtx, arrayBuffer) {
  return new Promise((resolve, reject) => {
    const promise = audioCtx.decodeAudioData(arrayBuffer, resolve, reject);
    if (promise && typeof promise.catch === 'function') {
      promise.catch(reject);
    }
  });
}

/**
 * Streams the coach's audio into Anam.
 *
 * Anam speaks everything it is handed as a single continuous stream and offers
 * no way to cancel part of it, so silencing a reply means interrupting the
 * persona — the caller's job — and making sure nothing further is pushed in
 * afterwards, which is what the epoch below guards. Only one reply is ever in
 * flight: the parent locks the chat for as long as the coach is speaking.
 */
function useAnamNudgytAgentAudioQueue({
  handleSendAudioChunk,
  handleEndAudioSequence
}) {
  const audioCtxRef = useRef(null);
  const queueRef = useRef(Promise.resolve());
  /**
   * Bumped by flushQueue(). Resetting the promise chain alone is not enough:
   * a chunk already awaiting decodeAudioData still resolves afterwards and
   * would push the stopped reply's audio into Anam.
   */
  const epochRef = useRef(0);

  const initAudioContext = useCallback(() => {
    if (!audioCtxRef.current) {
      const AudioContextClass =
        window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) {
        console.error('Web Audio API is not supported in this browser.');
        return null;
      }
      audioCtxRef.current = new AudioContextClass({
        sampleRate: anamConfig.sampleRate
      });
    }

    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume().catch((err) => {
        console.warn(
          '[AudioStream] AudioContext blocked. Requires user gesture.',
          err
        );
      });
    }

    return audioCtxRef.current;
  }, []);

  useEffect(() => {
    return () => {
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(console.error);
        audioCtxRef.current = null;
      }
    };
  }, []);

  const decodeToPcm = useCallback(
    async (base64Mp3) => {
      const ctx = initAudioContext();
      if (!ctx) return null;

      const binary = atob(base64Mp3);
      const mp3Bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        mp3Bytes[i] = binary.charCodeAt(i);
      }

      const audioBuffer = await decodeAudioDataSafe(ctx, mp3Bytes.buffer);

      let float32;
      if (audioBuffer.numberOfChannels > 1) {
        const length = audioBuffer.length;
        float32 = new Float32Array(length);
        for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
          const channel = audioBuffer.getChannelData(c);
          for (let i = 0; i < length; i++) float32[i] += channel[i];
        }
        const scale = 1 / audioBuffer.numberOfChannels;
        for (let i = 0; i < length; i++) float32[i] *= scale;
      } else {
        float32 = audioBuffer.getChannelData(0);
      }

      const int16 = new Int16Array(float32.length);
      for (let i = 0; i < float32.length; i++) {
        const clamped = Math.max(-1, Math.min(1, float32[i]));
        int16[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
      }

      return {
        pcmU8: new Uint8Array(int16.buffer, int16.byteOffset, int16.byteLength),
        durationMs: audioBuffer.duration * 1000
      };
    },
    [initAudioContext]
  );

  const processChunk = useCallback(
    async (base64Mp3, epoch) => {
      if (!base64Mp3) return;
      if (epoch !== epochRef.current) return;

      try {
        const decoded = await decodeToPcm(base64Mp3);
        // Re-checked after the await — the reply may have been stopped while
        // this chunk was decoding.
        if (epoch !== epochRef.current) return;
        if (decoded?.pcmU8?.byteLength) handleSendAudioChunk?.(decoded.pcmU8);
      } catch (err) {
        console.error(
          '[useAnamAudioStream] Error processing audio chunk:',
          err
        );
      }
    },
    [decodeToPcm, handleSendAudioChunk]
  );

  const enqueueChunk = useCallback(
    (base64Mp3) => {
      const epoch = epochRef.current;
      queueRef.current = queueRef.current.then(() =>
        processChunk(base64Mp3, epoch)
      );
    },
    [processChunk]
  );

  const enqueueEndSequence = useCallback(() => {
    const epoch = epochRef.current;
    queueRef.current = queueRef.current.then(() => {
      if (epoch !== epochRef.current) return;
      handleEndAudioSequence?.();
    });
  }, [handleEndAudioSequence]);

  /** Drops everything still on its way to Anam. */
  const flushQueue = useCallback(() => {
    epochRef.current += 1;
    queueRef.current = Promise.resolve();
  }, []);

  return {
    enqueueChunk,
    enqueueEndSequence,
    flushQueue,
    initAudio: initAudioContext
  };
}

export default useAnamNudgytAgentAudioQueue;
